import type { UseFetchOptions } from '#app'

import { toast } from 'vue3-toastify'

export type ErrorT = {
  code: string
  message: string
  success: 'success' | 'error'
  errors: Record<string, any>[]
}

export interface DataT<T> {
  data: T
  code: string
  message: string
  success: 'success' | 'error'
}

export type OptionT<T> = Omit<UseFetchOptions<DataT<T>>, 'method' | 'query' | 'body' | 'params' | 'headers' | 'baseURL'> & {
  injectToast?: boolean
}

// 写操作失败必须让用户感知；GET 的失败沿用旧行为，交给页面的 UIQuery 呈现
const isMutation = (method?: string) => ['POST', 'PUT', 'PATCH', 'DELETE'].includes(String(method).toUpperCase())

const handerResponse = (injectToast: boolean) => {
  const session = useSession()
  return {
    onRequest({ options }: any) {
      options.headers = new Headers(options.headers)
      // options.headers.set("Accept", "application/json");
      const token = session.getAuthToken()
      if (token) {
        options.headers.set('Authorization', `Bearer ${token}`)
      }
    },
    // 请求层错误拦截：超时、连接中断、DNS/防火墙拦截、CORS 等拿不到响应的场景
    // 不处理的话整条链路会静默失败，用户点击提交后没有任何反馈
    onRequestError({ options, error }: any) {
      if (import.meta.client && injectToast && isMutation(options?.method)) {
        toast.error(error?.message || 'Network error, please try again.')
      }
    },
    // 响应拦截
    onResponse({ response, options }: any) {
      if (response._data && response._data.status && import.meta.client) {
        // 后端业务失败返回的是 status: 'fail'，这里统一按「非 success 即失败」处理
        const state = response._data.status
        if (state !== 'success' && isMutation(options.method)) {
          const _data = response._data
          if (injectToast) {
            response.data = response._data
            displayErrors({ response })
          }
          return Promise.reject(_data)
        }
      }
      // 成功返回
      return response._data
    },
    // 错误处理
    onResponseError({ response }: any) {
      if (import.meta.client && injectToast) {
        // 网关返回 HTML/空响应时 _data 为空，需兜底，否则这里抛异常又会变成静默失败
        toast.error(response?._data?.message || `Request failed${response?.status ? ` (${response.status})` : ''}`)
      }
      return Promise.reject(response?._data)
    },
  }
}

async function fetch<D extends Record<string, any>>(url: string, option: UseFetchOptions<DataT<D>> & { injectToast?: boolean }) {
  const { public: { apiHost } } = useRuntimeConfig()
  const { injectToast, ..._options } = option

  const handerOptions = handerResponse(injectToast ?? true)

  return $fetch<DataT<D>>(url, {
    // @ts-ignore
    baseURL: `https://${apiHost}`, //'/api/test'
    ..._options,
    ...handerOptions,
  })
}

export async function fetchByGet<D extends {}>(url: string, params?: Record<string, any>, options?: OptionT<D>) {
  return fetch<D>(url, {
    method: 'GET',
    params,
    headers: {
      Accept: 'application/json',
    },
    ...options,
  })
}

export async function fetchByPost<D extends {}>(url: string, body?: Record<string, any>, options?: OptionT<D>) {
  return fetch<D>(url, {
    method: 'POST',
    body,
    headers: {
      Accept: 'application/json',
    },
    ...options,
  })
}

export async function fetchByPut<D extends {}>(url: string, body?: Record<string, any>, options?: OptionT<D>) {
  return fetch<D>(url, {
    method: 'PUT',
    body,
    headers: {
      Accept: 'application/json',
    },
    ...options,
  })
}

export async function fetchByPatch<D extends {}>(url: string, body?: Record<string, any>, options?: OptionT<D>) {
  return fetch<D>(url, {
    method: 'PATCH',
    body,
    headers: {
      Accept: 'application/json',
    },
    ...options,
  })
}

export async function fetchByDelete<D extends {}>(url: string, body?: Record<string, any>, options?: OptionT<D>) {
  return fetch<D>(url, {
    method: 'DELETE',
    body,
    headers: {
      Accept: 'application/json',
    },
    ...options,
  })
}

export async function fetchByFile<D extends {}>(url: string, body: FormData, options?: OptionT<D>) {
  return fetch<D>(url, {
    method: 'POST',
    body: body,
    headers: {
      Accept: 'application/json',
      // "Content-Type": "multipart/form-data",
    },
    ...options,
  })
}
